import React, { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, arabicError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useNotifications } from "../context/NotificationContext";
import { MessageCircle, Send, ArrowRight } from "lucide-react";
import DirectMessagesInbox from "../components/DirectMessagesInbox";
import MerchantPhotoThumb from "../components/MerchantPhotoThumb";
import { logger } from "../lib/logger";

const statusLabels = { pending: "قيد الانتظار", confirmed: "مؤكد", completed: "مكتمل", cancelled: "ملغى" };

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const WS_BASE = BACKEND_URL.replace(/^http/, "ws");

const Messages = () => {
  const { user } = useAuth();
  const { markMessagesRead } = useNotifications();
  const { reservationId } = useParams();
  const navigate = useNavigate();
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [dest, setDest] = useState("both");
  const [sending, setSending] = useState(false);
  const [connected, setConnected] = useState(false);
  const endRef = useRef(null);
  const wsRef = useRef(null);

  const loadReservations = useCallback(async () => {
    try {
      const r = await api.get("/reservations");
      setReservations(r.data);
      if (reservationId) {
        const match = r.data.find((x) => x.id === reservationId);
        if (match) setActive(match);
      }
    } catch (e) { toast.error(arabicError(e)); } finally { setLoading(false); }
  }, [reservationId]);

  const loadMessages = async (id) => {
    try {
      const r = await api.get(`/messages/${id}`, { params: { limit: 50 } });
      // Backend now returns { items, has_more, next_before } — items are oldest→newest
      setMessages(Array.isArray(r.data) ? r.data : (r.data?.items || []));
    }
    catch (e) { toast.error(arabicError(e)); }
  };

  useEffect(() => { loadReservations(); markMessagesRead(); }, [loadReservations, markMessagesRead]);

  // Real-time chat via WebSocket
  useEffect(() => {
    if (!active) return;

    // Initial history load
    loadMessages(active.id);

    const token = localStorage.getItem("imdad_token");
    if (!token) return;

    const url = `${WS_BASE}/api/ws/chat/${active.id}?token=${encodeURIComponent(token)}`;
    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onopen = () => setConnected(true);
    ws.onclose = () => setConnected(false);
    ws.onerror = () => setConnected(false);

    ws.onmessage = (event) => {
      try {
        const payload = JSON.parse(event.data);
        if (payload.type === "message" && payload.data?.reservation_id === active.id) {
          setMessages((prev) => {
            if (prev.some((m) => m.id === payload.data.id)) return prev;
            return [...prev, payload.data];
          });
          // User is viewing this thread → clear the unread-messages badge
          markMessagesRead();
        }
      } catch (err) {
        logger.warn("Failed to parse WS message:", err);
      }
    };

    // Heartbeat every 30s to keep connection alive
    const ping = setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) ws.send("ping");
    }, 30000);

    return () => {
      clearInterval(ping);
      try { ws.close(); } catch (err) { logger.warn("WS close failed:", err); }
      wsRef.current = null;
      setConnected(false);
    };
  }, [active]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const selectRes = (r) => {
    setActive(r);
    navigate(`/messages/${r.id}`, { replace: true });
  };

  const send = async (e) => {
    e?.preventDefault();
    if (!input.trim() || !active) return;
    setSending(true);
    try {
      const res = await api.post("/messages", { reservation_id: active.id, message: input, destination: dest });
      setInput("");
      // Optimistic append (WebSocket broadcast will also arrive but is deduped by id)
      setMessages((prev) => {
        if (prev.some((m) => m.id === res.data.id)) return prev;
        return [...prev, res.data];
      });
    } catch (e) { toast.error(arabicError(e)); } finally { setSending(false); }
  };

  const updateStatus = async (status) => {
    // Only confirm / cancel are wired from this page. Completion happens on
    // /reservations to keep a single fast path and avoid duplicate flows.
    if (status !== "confirmed" && status !== "cancelled") return;
    try {
      const r = await api.patch(`/reservations/${active.id}/status`, { status });
      setActive({ ...active, status: r.data.status });
      toast.success("تم تحديث الحالة");
      loadReservations();
    } catch (e) { toast.error(arabicError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="messages-page">
      <div>
        <h1 className="text-3xl md:text-4xl font-extrabold text-[#333333]">الرسائل والحجوزات</h1>
        <p className="text-[#666666] mt-1">{reservations.length} محادثة</p>
      </div>

      {/* Motivational banner */}
      <div className="bg-[#EBF4F6] border border-[#7AB2B2] rounded-xl p-3 text-[#09637E] font-bold text-center text-sm" data-testid="motivational-banner">
        التواصل المباشر يبني الثقة. كن واضحاً مع زبائنك من التجار.
      </div>

      {/* Story-reply direct messages (auto-hidden when empty) */}
      <DirectMessagesInbox reloadSignal={loading ? 0 : 1} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 h-[calc(100vh-16rem)] max-lg:h-[calc(100dvh-16rem)] max-lg:min-h-[24rem]">
        {/* List */}
        <div className={`${active ? "hidden lg:block" : ""} lg:col-span-1 bg-white border border-[#7AB2B2] rounded-xl overflow-y-auto`}>
          {loading ? (
            <div className="p-8 text-center text-[#666666]">جارٍ التحميل...</div>
          ) : reservations.length === 0 ? (
            <div className="p-8 text-center">
              <MessageCircle className="mx-auto text-[#7AB2B2] mb-3" size={40} />
              <p className="text-[#666666]">لا توجد محادثات بعد</p>
            </div>
          ) : (
            <ul data-testid="reservation-list">
              {reservations.map((r) => {
                const isActive = active?.id === r.id;
                const other = r.direction === "outgoing" ? r.owner_name : r.requester_name;
                return (
                  <li key={r.id}>
                    <button onClick={() => selectRes(r)} data-testid={`reservation-item-${r.id}`}
                      className={`w-full text-right p-4 border-b border-[#EBF4F6] hover:bg-[#EBF4F6] transition-colors ${isActive ? "bg-[#EBF4F6]" : ""}`}>
                      <div className="flex items-center gap-3">
                        {r.product_id && (
                          <div className="w-14 h-14 rounded-lg overflow-hidden shrink-0">
                            <MerchantPhotoThumb photoId={r.product_id} testid={`msg-list-thumb-${r.id}`} />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <div className="font-bold text-[#333333] truncate">{other}</div>
                            <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-[#09637E] text-white shrink-0">{r.direction === "incoming" ? "وارد" : "صادر"}</span>
                          </div>
                          <div className="text-xs text-[#666666] mt-1 truncate">{r.last_message || "لا توجد رسائل بعد"}</div>
                          <div className="text-xs text-[#09637E] font-bold mt-1">{statusLabels[r.status]}</div>
                        </div>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Chat */}
        <div className={`${!active ? "hidden lg:flex" : "flex"} lg:col-span-2 bg-white border border-[#7AB2B2] rounded-xl flex-col overflow-hidden`}>
          {!active ? (
            <div className="flex-1 flex items-center justify-center text-[#666666]">اختر محادثة لعرضها</div>
          ) : (
            <>
              <div className="border-b border-[#7AB2B2] p-4 flex items-center gap-3">
                <button onClick={() => { setActive(null); navigate("/messages"); }} className="lg:hidden p-2 rounded hover:bg-[#EBF4F6]" data-testid="back-to-list"><ArrowRight /></button>
                {active.product_id && (
                  <div className="w-12 h-12 rounded-lg overflow-hidden shrink-0">
                    <MerchantPhotoThumb photoId={active.product_id} testid={`msg-header-thumb-${active.id}`} />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-[#333333] truncate">
                    {active.direction === "outgoing" ? active.owner_name : active.requester_name}
                  </div>
                  <div className="text-xs text-[#666666]">
                    {active.product?.size} · {active.product?.color} · {active.product?.price?.toLocaleString("ar-SY")} ل.س
                  </div>
                </div>
                <span className="text-xs font-bold px-3 py-1 rounded-full bg-[#EBF4F6] text-[#09637E]">{statusLabels[active.status]}</span>
                <span className="flex items-center gap-1 text-xs text-[#666666]" data-testid="ws-status">
                  <span className={`w-2 h-2 rounded-full ${connected ? "bg-green-500" : "bg-gray-400"}`} />
                  {connected ? "مباشر" : "..."}
                </span>
              </div>

              {active.direction === "incoming" && active.status === "pending" && (
                <div className="bg-[#EBF4F6] p-3 flex gap-2 flex-wrap">
                  <button onClick={() => updateStatus("confirmed")} data-testid="confirm-reservation" className="h-10 px-4 rounded bg-[#09637E] hover:bg-[#088395] text-white font-bold text-sm">تأكيد الحجز</button>
                  <button onClick={() => updateStatus("cancelled")} data-testid="cancel-reservation" className="h-10 px-4 rounded border-2 border-red-500 text-red-500 hover:bg-red-50 font-bold text-sm">إلغاء</button>
                </div>
              )}
              {active.status === "confirmed" && (
                <div className="bg-[#EBF4F6] p-3 flex items-center justify-between gap-2 text-sm" data-testid="reservation-manage-link-wrap">
                  <span className="text-[#333333] font-bold">لإتمام الحجز أو إلغائه اذهب إلى صفحة الحجوزات.</span>
                  <button onClick={() => navigate("/reservations")} data-testid="go-to-reservations"
                    className="h-9 px-4 rounded-lg bg-[#09637E] hover:bg-[#088395] text-white font-bold text-xs shrink-0">
                    إدارة الحجز
                  </button>
                </div>
              )}
              {active.status === "completed" && (
                <div className="bg-green-50 border-y border-green-200 p-2 text-center text-green-700 font-extrabold text-sm" data-testid="reservation-completed-badge">
                  ✓ تم إتمام الحجز
                </div>
              )}
              {active.status === "cancelled" && (
                <div className="bg-red-50 border-y border-red-200 p-2 text-center text-red-600 font-extrabold text-sm" data-testid="reservation-cancelled-badge">
                  الحجز ملغى
                </div>
              )}

              <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="messages-list">
                {messages.length === 0 ? (
                  <div className="text-center text-[#666666] py-10">ابدأ المحادثة بإرسال رسالة</div>
                ) : messages.map((m) => {
                  const mine = m.sender_id === (user.merchant_id || user.id); // chat identity = the store
                  return (
                    <div key={m.id} className={`flex ${mine ? "justify-start" : "justify-end"}`}>
                      <div className={`max-w-[75%] rounded-2xl p-3 ${mine ? "bg-[#09637E] text-white" : "bg-[#EBF4F6] text-[#333333]"}`}>
                        <div className="text-xs font-bold opacity-80 mb-1">{m.sender_name}</div>
                        <div className="whitespace-pre-wrap break-words">{m.message}</div>
                        <div className="text-[10px] opacity-70 mt-1">{new Date(m.timestamp).toLocaleTimeString("ar-SY", { hour: "2-digit", minute: "2-digit" })}</div>
                      </div>
                    </div>
                  );
                })}
                <div ref={endRef} />
              </div>

              <form onSubmit={send} className="border-t border-[#7AB2B2] p-3 flex flex-wrap sm:flex-nowrap gap-2" data-testid="message-form">
                <select value={dest} onChange={(e) => setDest(e.target.value)} data-testid="message-destination-select"
                  title="وجهة إشعار الرسالة"
                  className="h-12 w-full sm:w-auto shrink-0 rounded-lg border border-[#7AB2B2] bg-white px-2 text-xs font-bold text-[#09637E] focus:outline-none focus:ring-2 focus:ring-[#09637E]">
                  <option value="both">التطبيق والبريد</option>
                  <option value="app">التطبيق فقط</option>
                  <option value="email">البريد فقط</option>
                </select>
                <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="اكتب رسالتك..." data-testid="message-input"
                  className="flex-1 min-w-0 h-12 rounded-lg border border-[#7AB2B2] bg-white px-4 focus:outline-none focus:ring-2 focus:ring-[#09637E]" />
                <button type="submit" disabled={sending || !input.trim()} data-testid="send-message-btn"
                  className="h-12 px-5 shrink-0 rounded-lg bg-[#09637E] hover:bg-[#088395] text-white font-bold flex items-center gap-1 disabled:opacity-60">
                  <Send size={18} /> إرسال
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Messages;
